/* eslint-disable */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@material-ui/core";
import { getStatus } from "../../services/approvalsApi";
import { ThreeDots } from "react-loader-spinner";

/* ╭────────────── Helpers ──────────────╮ */
const FLOW_MAP = {
  approved: "completed",
  rejected: "not approved",
  pending: "pending",
  queued: "queued",
  aborted : "skipped",
};

const RES_MAP = {
  APP: "completed",
  REJ: "not approved",
  REV: "needs review",
  ABT: "skipped",
};

const EVENT_TXT = {
  on_create: "initiative_selected.approvals_on_create",
  on_edit: "initiative_selected.approvals_on_edit",
  on_stage_change: "initiative_selected.approvals_on_stage_change",
};

const STAT_CLR = {
  completed: "green",
  "not approved": "#E04F5F",
  pending: "#9e9e9e",
  queued: "#bbbbbb",
  "needs review": "#F4C430",
  skipped       : "#cccccc",
};

function normalise(instances = []) {
  const byEv = { on_create: [], on_edit: [], on_stage_change: [] };

  instances.forEach((inst) => {
    /* ---------- passos ---------- */
    const steps = inst.steps.map((st) => {
      const approvers = st.approvals.map((ap, idx) => ({
        id: `${inst.id}-${st.order}-${idx}`,
        name: ap.approver,
        status: RES_MAP[ap.result] || "pending",
        comment: ap.comment,
      }));

      let stStatus = "completed";
      if (approvers.some((a) => a.status === "skipped"))       stStatus = "skipped";
      else if (approvers.some((a) => a.status === "not approved")) stStatus = "not approved";
      else if (approvers.some((a) => a.status === "needs review")) stStatus = "needs review";
      else if (approvers.some((a) => a.status === "pending"))      stStatus = "pending";

      return {
        order: st.order,
        name: st.name,
        status: stStatus,
        locked: false,
        approvers,
      };
    });

    /* ---------- bloqueia steps futuros em flows sequenciais ---------- */
    if (inst.sequential) {
      let foundOpen = false;
      steps.forEach((s) => {
        if (foundOpen && s.status === "pending") s.locked = true;
        if (["pending", "not approved"].includes(s.status))   foundOpen = true;
      });
    }

    /* ---------- status do flow ---------- */
    let flowStatus = "completed";
    if (steps.some((s) => s.status === "skipped"))        flowStatus = "skipped";
    else if (steps.some((s) => s.status === "not approved")) flowStatus = "not approved";
    else if (steps.some((s) => s.status === "needs review")) flowStatus = "needs review";
    else if (steps.some((s) => s.status === "pending"))      flowStatus = "pending";

    const archived = ["completed", "not approved", "aborted", "skipped"].includes(flowStatus);

    byEv[inst.trigger_event].push({
      id: inst.id,
      label: inst.flow_name,
      status: flowStatus,
      sequential: inst.sequential,
      priority: inst.priority,
      created_at: inst.created_at,   // mantém rastreio temporal
      archived,                      // flag para visual
      steps,
    });
  });

  /* ---------- ordena: vivos ↑  por data ↓  ---------- */
  Object.values(byEv).forEach((arr) => {
    arr.sort((a, b) => {
      if (a.archived !== b.archived) return a.archived ? 1 : -1;               // abertos primeiro
      return new Date(b.created_at) - new Date(a.created_at);                  // mais recentes primeiro
    });
  });

  return byEv;
}


/* ╭─────────────────── Main ───────────────────╮ */
export default function ApprovalsSection({ selectedInitiative, t, classes, clientColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(false);

  useEffect(() => {
    if (!selectedInitiative?.id) {
      setData(null);
      return;
    }

    setLoad(true);
    getStatus(selectedInitiative.id)
      .then((d) => setData(normalise(d.instances)))
      .finally(() => setLoad(false));
  }, [selectedInitiative]);

  if (loading) return (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
    }}
  >
    <ThreeDots
      color={clientColor}
      height={80}
      width={80}
      ariaLabel="loading"
    />
  </div>);

  if (!data || Object.values(data).every((arr) => !arr.length))
    return (
      <div style={{ padding: 12, fontStyle: "italic", fontSize: 13 }}>
        {t("initiative_selected.no_approvals")}
      </div>
    );

  const blocks = ["on_create", "on_edit", "on_stage_change"];

  return (
    <div className={classes.approvalDiv}>
      {blocks.map(
        (ev) =>
          !!data[ev]?.length && (
            <EventBlock
              key={ev}
              label={t(EVENT_TXT[ev])}
              flows={data[ev]}
              t={t}
              classes={classes}
            />
          ),
      )}
    </div>
  );
}

ApprovalsSection.propTypes = {
  selectedInitiative: PropTypes.shape({ id: PropTypes.string }),
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  clientColor: PropTypes.string.isRequired,
};

/* ╭────────── EventBlock ──────────╮ */
function EventBlock({ label, flows, t, classes }) {
  // separa “ativos” (≠ archived) e “arquivados”
  const activeFlows   = flows.filter(f => !f.archived);
  const archivedFlows = flows.filter(f =>  f.archived);

  return (
    <div style={{ marginBottom: 16 }}>
      <h3
        style={{
          margin: "0 0 4px 2px",
          fontSize: 13,
          fontWeight: 600,
          color: "#444",
        }}
      >
        {label}
      </h3>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {/* ─────── NOVOS / EM ANDAMENTO ─────── */}
        {activeFlows.map(f => (
          <FlowCard key={f.id} flow={f} t={t} />
        ))}

        {/* quebra de linha se houver arquivados */}
        {archivedFlows.length > 0 && (
          <span style={{ flexBasis: "100%", height: 0 }} />
        )}

        {/* ─────── ARQUIVADOS (fechados) ─────── */}
        {archivedFlows.map(f => (
          <FlowCard key={f.id} flow={f} t={t} />
        ))}
      </div>
    </div>
  );
}


EventBlock.propTypes = {
  label: PropTypes.string.isRequired,
  flows: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

/* ╭────────── FlowCard Compacta ───────────╮ */function FlowCard({ flow, t }) {
  // ① “archived” é soberano  → sempre semi-transparente
  // ② se NÃO for archived mas estiver skipped → também semi-transparente
  const isArchived      = flow.archived;                // novo
  const isSkippedOnly   = !isArchived && flow.status === "skipped";

  const cardOpacity     = (isArchived || isSkippedOnly) ? 0.45 : 1;

  return (
    <div
      style={{
        width: 240,
        borderLeft: `4px solid ${STAT_CLR[flow.status]}`,
        background: "#fafafa",
        padding: "10px 14px",
        fontSize: 13,
        lineHeight: 1.4,
        borderRadius: 6,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        opacity: cardOpacity,
      }}
    >
      {/* ── cabeçalho ───────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            color: "#333",
            maxWidth: "calc(100% - 20px)",
            textDecoration: isArchived ? "line-through" : "none", // opcional
          }}
        >
          {flow.label}
        </span>

        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: STAT_CLR[flow.status],
            marginTop: 4,
            opacity: isArchived ? 0.6 : 1,
          }}
        />
      </div>

      {/* ── passos ──────────────────────────────────────────── */}
      {flow.steps.map((st) => (
        <div
          key={st.order}
          style={{
            display: "flex",
            flexWrap: "wrap",
            marginBottom: 6,
            opacity: st.locked ? 0.4 : 1,
            fontSize: 12,
          }}
        >
        
          {st.approvers.length > 0 ? (
            st.approvers.map((ap) => (
              <Tooltip title={ap.comment || ""} key={ap.id} arrow>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: STAT_CLR[ap.status],
                      marginRight: 4,
                    }}
                  />
                  {ap.name}
                </span>
              </Tooltip>
            ))
          ) : (
            <span style={{ color: "#aaa" }}>
              {t("initiative_selected.no_approvers")}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}


FlowCard.propTypes = {
  flow: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};
